import { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import usePlanStore from 'store/planStore';
import { getOfflineSetup } from '../../../../../../../../api/services/mediaPlanning/offline/setup';
import { useError } from '../../../../../common/Errors/ErrorContext/ErrorContext';

const useTVSetup = (country: string) => {
  const {
    countryForms,
    savedCountryForms,
    savedGeneralInfoState,
    selectedCountryISOCode,
    selectedCountry,
    setSelectedCountryISOCode,
    updateTVConfig,
  } = usePlanStore();

  const { showError } = useError();

  const [isoCode, setIsoCode] = useState<string | null>(selectedCountryISOCode);
  const { id } = useParams();
  const daysInWeek = 7;

  const formData = countryForms[selectedCountry!];

  useEffect(() => {
    if (country) {
      setSelectedCountryISOCode(country);
    }
  }, [country, setSelectedCountryISOCode]);

  useEffect(() => {
    setIsoCode(selectedCountryISOCode);
  }, [selectedCountryISOCode]);

  const {
    mutate: fetchTVSetup,
    isLoading,
    data: response,
    error,
  } = useMutation({
    mutationFn: (tvTargetGroup?: string) => {
      if (!isoCode || !selectedCountry) {
        return Promise.reject(
          new Error('Missing required parameters for TV setup.')
        );
      }

      return getOfflineSetup(
        savedCountryForms[selectedCountry].campaignScenario ?? '',
        savedGeneralInfoState?.category ?? '',
        isoCode,
        savedGeneralInfoState?.division ?? '',
        tvTargetGroup ??
          countryForms[selectedCountry].offline?.tv?.targetGroup ??
          null,
        countryForms[selectedCountry!].targetGroup
      );
    },
    onError: (err: any) => {
      const errorMessage =
        err?.response?.data?.message || err.message || 'Unknown error';
      showError(errorMessage);
    },
    onSuccess: (res, tvTargetGroup) => {
      const startDate = moment(
        formData?.startDate,
        ['DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'],
        true
      );

      const endDate = startDate
        .clone()
        .add((res.data.recoActWeeks ?? 0) * daysInWeek - 1, 'days')
        .format('DD-MM-YYYY');

      updateTVConfig(selectedCountry!, {
        minCampaignLength: res.data.minActWeeks,
        maxCampaignLength: res.data.maxActWeeks,
        recommendedCampaignLength: res.data.recoActWeeks,
        weeklyWeight: {
          min: res.data.minWeeklyGrp,
          max: res.data.maxWeeklyGrp,
          current: res.data.recoWeeklyGrp,
        },
        startDate: formData.startDate,
        endDate,
        targetGroup:
          tvTargetGroup ??
          res.data.targetGroups?.find(
            (group) => group === res.data.currentTargetGroup ?? null
          ),
        recommendedTargetGroup: res.data.defaultTargetGroup,
        targetGroups: res.data.targetGroups,
      });
    },
  });

  const tvSetup = response?.data ?? undefined;

  return { fetchTVSetup, data: tvSetup, isLoading, error };
};

export default useTVSetup;
